import React, { useMemo, useState } from 'react';

import { useSearchParams } from 'react-router-dom';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Form, Input, message, Modal, Select, Table, Tabs } from 'antd';
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table';

import { createRole, createUser, deleteUser, listRoles, listUsers, updateUserRoles } from '@/shared/api/users';
import type {
  CreateRoleRequest,
  CreateUserRequest,
  RoleResponse,
  UpdateUserRolesRequest,
  UserListItemResponse,
} from '@/shared/api/users';
import { COLOR } from '@/shared/design-tokens/design-tokens';
import { FORM_LABELS, PAGINATION_OPTIONS } from '@/shared/lib/constants';
import { userKeys } from '@/shared/lib/query/queryKeys';
import { highlightText } from '@/shared/lib/text';
import type { BadgeItem } from '@/shared/types/badge';
import { BadgeList } from '@/shared/ui/BadgeList';
import { Button } from '@/shared/ui/Button';
import { ConfirmModal } from '@/shared/ui/ConfirmModal';
import { Icon } from '@/shared/ui/Icon';
import { SearchInput } from '@/shared/ui/SearchInput';

import { RolePermissionModal } from '@/features/user-management';

import styles from './UserManagementPage.module.css';

const USER_COLUMN_WIDTHS = {
  USERNAME: 250,
  EMAIL: 400,
  LAST_NAME: 250,
  FIRST_NAME: 250,
} as const;

const ROLE_COLUMN_WIDTHS = {
  NAME: 200,
  DESCRIPTION: 600,
} as const;

// 시스템 권한 (화면에 표시하지 않음)
const SYSTEM_ROLES = ['default-roles-dev-cyclone', 'offline_access', 'uma_authorization'] as const;

type SystemRole = (typeof SYSTEM_ROLES)[number];

const isSystemRole = (role: string): role is SystemRole => {
  return (SYSTEM_ROLES as readonly string[]).includes(role);
};

export const UserManagementPage: React.FC = () => {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') || 'users';

  const [searchQuery, setSearchQuery] = useState('');
  const [roleSearchQuery, setRoleSearchQuery] = useState('');
  const [isUserModalOpen, setIsUserModalOpen] = useState(false);
  const [isRoleModalOpen, setIsRoleModalOpen] = useState(false);
  const [isDeleteUserModalOpen, setIsDeleteUserModalOpen] = useState(false);
  const [isRolePermissionModalOpen, setIsRolePermissionModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<UserListItemResponse | null>(null);
  const [selectedRole, setSelectedRole] = useState<RoleResponse | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [initialEditFormValues, setInitialEditFormValues] = useState<{ realmRoles: string[] } | null>(null);
  const [hasEditFormChanges, setHasEditFormChanges] = useState(false);

  const [userForm] = Form.useForm();
  const [roleForm] = Form.useForm();

  const { data: usersData, isLoading: isLoadingUsers } = useQuery({
    queryKey: userKeys.lists({ query: searchQuery || undefined, page: currentPage, size: pageSize }),
    queryFn: () => listUsers({ page: currentPage, size: pageSize, query: searchQuery || undefined }),
    enabled: activeTab === 'users',
    staleTime: 0,
  });

  const { data: rolesData, isLoading: isLoadingRoles } = useQuery({
    queryKey: userKeys.roles(),
    queryFn: () => listRoles(),
    enabled: activeTab === 'roles',
    staleTime: 0,
  });

  const { data: allRolesData } = useQuery({
    queryKey: userKeys.allRoles(),
    queryFn: () => listRoles(),
  });

  const createUserMutation = useMutation({
    mutationFn: createUser,
    onSuccess: () => {
      message.success('사용자가 생성되었습니다');
      setIsUserModalOpen(false);
      userForm.resetFields();
      setCurrentPage(1);
      setSelectedUserIds([]);
      queryClient.invalidateQueries({ queryKey: userKeys.listsPrefix() });
    },
    onError: () => {
      message.error('사용자 생성에 실패했습니다');
    },
  });

  const createRoleMutation = useMutation({
    mutationFn: createRole,
    onSuccess: () => {
      message.success('권한이 생성되었습니다');
      setIsRoleModalOpen(false);
      roleForm.resetFields();
      queryClient.invalidateQueries({ queryKey: userKeys.rolesPrefix() });
    },
    onError: () => {
      message.error('권한 생성에 실패했습니다');
    },
  });

  const updateUserRolesMutation = useMutation({
    mutationFn: ({ id, request }: { id: number; request: UpdateUserRolesRequest }) => updateUserRoles(id, request),
    onSuccess: () => {
      message.success('사용자 권한이 수정되었습니다');
      setEditingUser(null);
      userForm.resetFields();
      setInitialEditFormValues(null);
      setHasEditFormChanges(false);
      queryClient.invalidateQueries({ queryKey: userKeys.listsPrefix() });
    },
    onError: () => {
      message.error('사용자 권한 수정에 실패했습니다');
    },
  });

  const deleteUsersMutation = useMutation({
    mutationFn: async (userIds: number[]) => {
      const results = await Promise.allSettled(userIds.map((id) => deleteUser(id)));

      const successCount = results.filter((r) => r.status === 'fulfilled').length;
      const failCount = results.filter((r) => r.status === 'rejected').length;

      if (failCount > 0 && successCount === 0) {
        throw new Error('모든 사용자 삭제 실패');
      }

      return { successCount, failCount };
    },
    onSuccess: (data) => {
      const { successCount, failCount } = data;

      if (failCount > 0) {
        message.warning(`${successCount}명 삭제 성공, ${failCount}명 실패`);
      } else {
        message.success('사용자가 삭제되었습니다');
      }

      setIsDeleteUserModalOpen(false);
      setSelectedUserIds([]);
      setCurrentPage(1);
      queryClient.invalidateQueries({ queryKey: userKeys.listsPrefix() });
    },
    onError: () => {
      message.error('사용자 삭제에 실패했습니다');
    },
  });

  const allUserColumns: ColumnsType<UserListItemResponse> = useMemo(
    () => [
      {
        title: '사용자명',
        dataIndex: 'username',
        key: 'username',
        width: USER_COLUMN_WIDTHS.USERNAME,
        ellipsis: true,
        render: (text: string) => {
          if (!searchQuery.trim()) return text;
          return highlightText(text, searchQuery, { className: 'highlight' });
        },
      },
      {
        title: '이메일',
        dataIndex: 'email',
        key: 'email',
        width: USER_COLUMN_WIDTHS.EMAIL,
        ellipsis: true,
        render: (text: string) => {
          if (!searchQuery.trim()) return text;
          return highlightText(text, searchQuery, { className: 'highlight' });
        },
      },
      {
        title: '성',
        dataIndex: 'lastName',
        key: 'lastName',
        width: USER_COLUMN_WIDTHS.LAST_NAME,
        ellipsis: true,
        render: (text: string) => {
          if (!searchQuery.trim()) return text;
          return highlightText(text, searchQuery, { className: 'highlight' });
        },
      },
      {
        title: '이름',
        dataIndex: 'firstName',
        key: 'firstName',
        width: USER_COLUMN_WIDTHS.FIRST_NAME,
        ellipsis: true,
        render: (text: string) => {
          if (!searchQuery.trim()) return text;
          return highlightText(text, searchQuery, { className: 'highlight' });
        },
      },
      {
        title: '역할',
        dataIndex: 'roles',
        key: 'roles',
        render: (roles: string[]) => {
          // 시스템 권한 필터링
          const filteredRoles = roles.filter((role) => !isSystemRole(role));

          const badgeItems: BadgeItem[] = filteredRoles.map((role) => ({
            tagFQN: role,
            name: role,
            displayName: role,
          }));
          return <BadgeList items={badgeItems} showIcon={false} />;
        },
      },
    ],
    [searchQuery]
  );

  const visibleUserColumns = useMemo(() => {
    const columnsByKey: Record<string, ColumnsType<UserListItemResponse>[0]> = {};
    allUserColumns.forEach((col) => {
      const key = col.key as string;
      columnsByKey[key] = col;
    });

    return allUserColumns.map((col) => columnsByKey[col.key as string]);
  }, [allUserColumns]);

  const allRoleColumns: ColumnsType<RoleResponse> = useMemo(
    () => [
      {
        title: '권한명',
        dataIndex: 'name',
        key: 'name',
        width: ROLE_COLUMN_WIDTHS.NAME,
        render: (text: string) => {
          if (!roleSearchQuery.trim()) return text;
          return highlightText(text, roleSearchQuery, { className: 'highlight' });
        },
      },
      {
        title: '설명',
        dataIndex: 'description',
        key: 'description',
        width: ROLE_COLUMN_WIDTHS.DESCRIPTION,
        render: (text: string | null) => text || '-',
      },
    ],
    [roleSearchQuery]
  );

  const visibleRoleColumns = allRoleColumns;

  const filteredRolesData = useMemo(() => {
    if (!rolesData?.roles) return [];

    const systemFilteredRoles = rolesData.roles.filter((role) => !isSystemRole(role.name));

    if (!roleSearchQuery.trim()) return systemFilteredRoles;

    return systemFilteredRoles.filter((role) => role.name.toLowerCase().includes(roleSearchQuery.toLowerCase()));
  }, [rolesData, roleSearchQuery]);

  const roleSelectOptions = useMemo(
    () =>
      (allRolesData?.roles ?? [])
        .filter((role) => !isSystemRole(role.name))
        .map((role) => ({
          label: role.name,
          value: role.name,
        })),
    [allRolesData?.roles]
  );

  const usersPaginationConfig: TablePaginationConfig = {
    current: currentPage,
    pageSize: pageSize,
    total: usersData?.pagination.totalCount || 0,
    pageSizeOptions: [...PAGINATION_OPTIONS],
    showSizeChanger: true,
    locale: { items_per_page: '' },
    showTotal: (total: number, range: [number, number]) => `(${total}개 중 ${range[0]}-${range[1]})`,
    onChange: (page, size) => {
      setCurrentPage(page);
      setSelectedUserIds([]);
      if (size !== pageSize) {
        setPageSize(size);
        setCurrentPage(1);
      }
    },
  };

  const rolesPaginationConfig: TablePaginationConfig = {
    pageSize: 20,
    total: filteredRolesData.length,
    pageSizeOptions: [...PAGINATION_OPTIONS],
    showSizeChanger: true,
    locale: { items_per_page: '' },
    showTotal: (total: number, range: [number, number]) => `(${total}개 중 ${range[0]}-${range[1]})`,
  };

  const handleTabChange = (key: string) => {
    setSearchParams({ tab: key });
    setSearchQuery('');
    setRoleSearchQuery('');
    setCurrentPage(1);
    setSelectedUserIds([]);
  };

  const handleCreateUser = async () => {
    try {
      const values = await userForm.validateFields();
      const request: CreateUserRequest = {
        username: values.username,
        email: values.email || null,
        firstName: values.firstName || null,
        lastName: values.lastName || null,
        password: values.password || null,
        enabled: true,
        realmRoles: values.realmRoles || [],
      };
      createUserMutation.mutate(request);
    } catch (error) {
      console.error('Form validation failed:', error);
    }
  };

  const handleCreateRole = async () => {
    try {
      const values = await roleForm.validateFields();
      const request: CreateRoleRequest = {
        name: values.name,
        description: values.description || null,
      };
      createRoleMutation.mutate(request);
    } catch (error) {
      console.error('Form validation failed:', error);
    }
  };

  const handleUpdateUser = async () => {
    if (!editingUser || editingUser.id === null) {
      message.error('사용자 정보를 찾을 수 없습니다');
      return;
    }

    try {
      const values = await userForm.validateFields();
      const request: UpdateUserRolesRequest = {
        roleNames: values.realmRoles || [],
      };
      updateUserRolesMutation.mutate({ id: editingUser.id, request });
    } catch (error) {
      console.error('Form validation failed:', error);
    }
  };

  const handleDeleteUsers = async () => {
    const userIdsToDelete = usersData?.users
      .filter((user) => selectedUserIds.includes(user.userId) && user.id !== null)
      .map((user) => user.id as number);

    if (!userIdsToDelete || userIdsToDelete.length === 0) {
      message.error('삭제할 사용자를 찾을 수 없습니다');
      return;
    }

    deleteUsersMutation.mutate(userIdsToDelete);
  };

  const handleDeleteModalClose = () => {
    setIsDeleteUserModalOpen(false);
    setSelectedUserIds([]);
  };

  const renderUsersTab = () => (
    <div className={styles.tabContent}>
      <div className={styles.toolbar}>
        <SearchInput
          placeholder="사용자명, 이메일, 이름 검색..."
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value);
            setCurrentPage(1);
          }}
        />
        <div className={styles.toolbarRight}>
          <Button onClick={() => setIsUserModalOpen(true)}>사용자 추가</Button>
          <Button
            variant="outlined"
            onClick={() => setIsDeleteUserModalOpen(true)}
            disabled={selectedUserIds.length === 0}>
            사용자 삭제
          </Button>
        </div>
      </div>

      <Table<UserListItemResponse>
        size="small"
        columns={visibleUserColumns}
        dataSource={usersData?.users || []}
        rowKey="userId"
        loading={isLoadingUsers}
        pagination={usersPaginationConfig}
        scroll={{
          x: usersData?.users && usersData.users.length > 0 ? 'max-content' : undefined,
          y: 'max(calc(100vh - 340px), 628px)',
        }}
        className={styles.userTable}
        onRow={(record) => ({
          onClick: () => {
            setEditingUser(record);
            const filteredRoles = record.roles.filter((role) => !isSystemRole(role));
            const formValues = {
              username: record.username,
              email: record.email,
              lastName: record.lastName,
              firstName: record.firstName,
              realmRoles: filteredRoles,
            };
            userForm.setFieldsValue(formValues);
            setInitialEditFormValues({ realmRoles: filteredRoles });
            setHasEditFormChanges(false);
          },
        })}
        rowSelection={{
          selectedRowKeys: selectedUserIds,
          columnWidth: 60,
          onChange: (selectedRowKeys) => {
            setSelectedUserIds(selectedRowKeys as string[]);
          },
          columnTitle: (originalElement: React.ReactNode) => {
            return (
              <div
                className={styles.checkboxColumn}
                style={{
                  visibility: selectedUserIds.length > 0 ? 'visible' : 'hidden',
                }}>
                {originalElement}
              </div>
            );
          },
        }}
      />
    </div>
  );

  const renderRolesTab = () => (
    <div className={styles.tabContent}>
      <div className={styles.toolbar}>
        <SearchInput
          placeholder="권한명 검색..."
          value={roleSearchQuery}
          onChange={(e) => setRoleSearchQuery(e.target.value)}
        />
        <div className={styles.toolbarRight}>
          <Button onClick={() => setIsRoleModalOpen(true)}>권한 추가</Button>
        </div>
      </div>

      <Table<RoleResponse>
        size="small"
        columns={visibleRoleColumns}
        dataSource={filteredRolesData}
        rowKey="id"
        loading={isLoadingRoles}
        pagination={rolesPaginationConfig}
        scroll={{
          x: filteredRolesData.length > 0 ? 'max-content' : undefined,
          y: 'max(calc(100vh - 340px), 628px)',
        }}
        className={styles.roleTable}
        onRow={(record) => ({
          onClick: () => {
            setSelectedRole(record);
            setIsRolePermissionModalOpen(true);
          },
        })}
      />
    </div>
  );

  return (
    <div className={styles.userManagementPage}>
      <div className={styles.header}>
        <div className={styles.title}>사용자 관리</div>
        <div className={styles.description}>시스템 사용자와 권한을 관리할 수 있습니다.</div>
      </div>

      <div className={styles.content}>
        <Tabs
          activeKey={activeTab}
          onChange={handleTabChange}
          tabPosition="left"
          items={[
            {
              key: 'users',
              label: '사용자',
              children: renderUsersTab(),
            },
            {
              key: 'roles',
              label: '권한',
              children: renderRolesTab(),
            },
          ]}
        />
      </div>

      <Modal
        title="사용자 추가"
        open={isUserModalOpen}
        onOk={handleCreateUser}
        onCancel={() => {
          setIsUserModalOpen(false);
          userForm.resetFields();
        }}
        okText="생성"
        cancelText="취소"
        confirmLoading={createUserMutation.isPending}
        destroyOnHidden>
        <Form form={userForm} layout="vertical" initialValues={{ realmRoles: [] }}>
          <Form.Item
            name="username"
            label="사용자명"
            rules={[
              { required: true, message: '사용자명을 입력하세요.' },
              { max: 255, message: '사용자명은 최대 255자까지 입력할 수 있습니다.' },
              {
                pattern: /^[a-zA-Z0-9._-]+$/,
                message: '사용자명은 영문, 숫자, 특수문자(.-_)만 사용할 수 있습니다.',
              },
            ]}
            validateFirst>
            <Input placeholder="예: john.doe123" />
          </Form.Item>
          <Form.Item
            name="email"
            label="이메일"
            rules={[
              { required: true, message: '이메일을 입력하세요.' },
              { type: 'email', message: '올바른 이메일 형식이 아닙니다.' },
            ]}
            validateFirst>
            <Input placeholder="이메일을 입력하세요" />
          </Form.Item>
          <Form.Item name="lastName" label="성" rules={[{ required: true, message: '성을 입력하세요.' }]}>
            <Input placeholder="성을 입력하세요" />
          </Form.Item>
          <Form.Item name="firstName" label="이름" rules={[{ required: true, message: '이름을 입력하세요.' }]}>
            <Input placeholder="이름을 입력하세요" />
          </Form.Item>
          <Form.Item
            name="password"
            label="초기 비밀번호"
            rules={[
              { required: true, message: '초기 비밀번호를 입력하세요.' },
              { min: 8, message: '비밀번호는 최소 8자 이상이어야 합니다.' },
              { max: 20, message: '비밀번호는 최대 20자까지 입력할 수 있습니다.' },
            ]}
            validateFirst>
            <Input.Password placeholder="초기 비밀번호를 입력하세요 (8~20자)" />
          </Form.Item>
          <Form.Item
            name="realmRoles"
            label="역할"
            rules={[
              {
                required: true,
                validator: (_, value) => {
                  if (!value || value.length === 0) {
                    return Promise.reject(new Error('최소 1개의 역할을 선택하세요.'));
                  }
                  return Promise.resolve();
                },
              },
            ]}>
            <Select mode="multiple" placeholder="역할을 선택하세요" options={roleSelectOptions} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="사용자 수정"
        open={!!editingUser}
        onOk={handleUpdateUser}
        onCancel={() => {
          setEditingUser(null);
          userForm.resetFields();
          setInitialEditFormValues(null);
          setHasEditFormChanges(false);
        }}
        okText="수정"
        cancelText="취소"
        confirmLoading={updateUserRolesMutation.isPending}
        okButtonProps={{ disabled: !hasEditFormChanges }}
        destroyOnHidden>
        <Form
          form={userForm}
          layout="vertical"
          onValuesChange={(_, allValues) => {
            if (!initialEditFormValues) return;

            const currentRoles = [...((allValues.realmRoles || []) as string[])].sort();
            const initialRoles = [...(initialEditFormValues.realmRoles || [])].sort();

            const hasChanges =
              currentRoles.length !== initialRoles.length ||
              currentRoles.some((role, index) => role !== initialRoles[index]);

            setHasEditFormChanges(hasChanges);
          }}>
          <Form.Item name="username" label="사용자명">
            <Input disabled />
          </Form.Item>
          <Form.Item
            name="email"
            label="이메일"
            rules={[
              { required: true, message: '이메일을 입력하세요.' },
              { type: 'email', message: '올바른 이메일 형식이 아닙니다.' },
            ]}
            validateFirst>
            <Input placeholder="이메일을 입력하세요" />
          </Form.Item>
          <Form.Item name="lastName" label="성" rules={[{ required: true, message: '성을 입력하세요.' }]}>
            <Input placeholder="성을 입력하세요" />
          </Form.Item>
          <Form.Item name="firstName" label="이름" rules={[{ required: true, message: '이름을 입력하세요.' }]}>
            <Input placeholder="이름을 입력하세요" />
          </Form.Item>
          <Form.Item
            name="realmRoles"
            label="역할"
            rules={[
              {
                required: true,
                validator: (_, value) => {
                  if (!value || value.length === 0) {
                    return Promise.reject(new Error('최소 1개의 역할을 선택하세요.'));
                  }
                  return Promise.resolve();
                },
              },
            ]}>
            <Select mode="multiple" placeholder="역할을 선택하세요" options={roleSelectOptions} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="권한 추가"
        open={isRoleModalOpen}
        onOk={handleCreateRole}
        onCancel={() => {
          setIsRoleModalOpen(false);
          roleForm.resetFields();
        }}
        okText="생성"
        cancelText="취소"
        confirmLoading={createRoleMutation.isPending}
        destroyOnHidden>
        <Form form={roleForm} layout="vertical">
          <Form.Item
            name="name"
            label="권한명"
            rules={[
              { required: true, message: '권한명을 입력하세요.' },
              { max: 255, message: '권한명은 최대 255자까지 입력할 수 있습니다.' },
            ]}>
            <Input placeholder="권한명을 입력하세요" />
          </Form.Item>
          <Form.Item name="description" label="설명" rules={[{ required: true, message: '설명을 입력하세요.' }]}>
            <Input.TextArea
              className={styles.textarea}
              placeholder="권한 설명을 입력해 주세요"
              maxLength={300}
              aria-label={FORM_LABELS.DESCRIPTION}
            />
          </Form.Item>
        </Form>
      </Modal>

      <ConfirmModal
        open={isDeleteUserModalOpen}
        icon={<Icon name="trash" size={32} color={COLOR.GRAY[10]} />}
        message={
          <>
            선택한 {selectedUserIds.length}명의 사용자를 삭제합니다.
            <br />
            삭제한 사용자는 복구할 수 없습니다.
            <br />
            삭제하시겠습니까?
          </>
        }
        confirmText="삭제"
        cancelText="취소"
        confirmButtonProps={{ danger: true }}
        onConfirm={handleDeleteUsers}
        onCancel={handleDeleteModalClose}
      />

      <RolePermissionModal
        open={isRolePermissionModalOpen}
        role={selectedRole}
        onClose={() => {
          setIsRolePermissionModalOpen(false);
          setSelectedRole(null);
        }}
      />
    </div>
  );
};
